import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';

import { api } from '@/shared/api/http';
import type { CursorPage, FileRef, IsoDateTime, PagedResult } from '@/shared/api/types';
import type { CommentItem } from '@/shared/components/comments';

/*
 * Disk API (backend module `disk`, route root /api/disk — docs/modules/disk.md). Query keys start with the module id;
 * `invalidateDisk` refreshes everything a change may have touched (mutations and realtime events use it).
 */

export type StorageKind = 'user' | 'group' | 'company';
export type ItemType = 'folder' | 'file';
/** Effective access of the caller (ordered: each includes the previous). */
export type DiskRole = 'none' | 'view' | 'edit' | 'manage';
export type ShareRole = 'viewer' | 'editor';
export type ShareTargetType = 'user' | 'department' | 'group';
export type ConflictMode = 'fail' | 'rename' | 'replace' | 'skip' | 'version';
export type ContentKind =
  | 'other'
  | 'image'
  | 'video'
  | 'audio'
  | 'pdf'
  | 'text'
  | 'document'
  | 'spreadsheet'
  | 'presentation'
  | 'archive';
export type ItemSort = 'name' | 'updated' | 'size' | 'type';
export type SortDirection = 'asc' | 'desc';

export interface Person {
  id: string;
  fullName: string;
  avatarUrl: string | null;
  position: string | null;
  departmentId?: string | null;
}

export interface DiskGroup {
  id: string;
  name: string;
  avatarUrl: string | null;
  isProject: boolean;
  visibility: string;
}

export interface Breadcrumb {
  id: string;
  name: string;
}

export interface StorageUsage {
  usedBytes: number;
  trashBytes: number;
  quotaBytes: number;
  fileCount: number;
}

export interface Storage {
  id: string;
  kind: StorageKind;
  rootFolderId: string;
  owner: Person | null;
  group: DiskGroup | null;
  myRole: DiskRole;
  isArchived: boolean;
  usage: StorageUsage | null;
}

export interface Storages {
  my: Storage;
  company: Storage;
  groups: Storage[];
}

export interface UsageByKind {
  kind: ContentKind;
  bytes: number;
  count: number;
}

export interface UsageDetails {
  usedBytes: number;
  trashBytes: number;
  versionBytes: number;
  quotaBytes: number;
  fileCount: number;
  kinds: UsageByKind[];
}

export interface DiskFile {
  fileId: string;
  url: string;
  thumbnailUrl: string | null;
  downloadUrl: string;
}

export interface DiskPermissions {
  canEdit: boolean;
  canRename: boolean;
  canDelete: boolean;
  canMove: boolean;
  canShare: boolean;
}

export interface DiskItem {
  id: string;
  storageId: string;
  parentId: string | null;
  type: ItemType;
  name: string;
  extension: string | null;
  size: number;
  contentType: string | null;
  versionNumber: number;
  childCount: number | null;
  hasSubfolders: boolean;
  createdAt: IsoDateTime;
  createdBy: Person | null;
  updatedAt: IsoDateTime;
  updatedBy: Person | null;
  file: DiskFile | null;
  myRole: DiskRole;
  permissions: DiskPermissions;
  isShared: boolean;
  hasPublicLink: boolean;
  deletedAt: IsoDateTime | null;
  /** Search hits deep inside a folder: the folder they are in. */
  parentName?: string | null;
}

export interface Folder {
  folder: DiskItem;
  storage: Storage;
  path: Breadcrumb[];
  viaShare: boolean;
  realtimeTopic: string;
}

export interface PublicLink {
  token: string;
  url: string;
  expiresAt: IsoDateTime | null;
  createdAt: IsoDateTime;
  createdBy: Person | null;
  downloadCount: number;
  /** The link asks visitors for a password (the password itself is never returned). */
  hasPassword?: boolean;
  /** How many downloads the link allows; `null`/absent = no limit. */
  maxDownloads?: number | null;
}

/** Creates a public link or changes it: `expiresAt` is always applied, the rest only when present. */
export interface SetPublicLinkBody {
  expiresAt: string | null;
  password?: string;
  removePassword?: boolean;
  maxDownloads?: number;
  removeMaxDownloads?: boolean;
}

/** `GET /api/disk/settings/public-links` (Administration → Disk): who may create or change public links. */
export interface PublicLinkPolicy {
  publicLinks: 'all' | 'admins' | 'off';
  canPublish: boolean;
}

export interface ItemDetails {
  item: DiskItem;
  storage: Storage;
  path: Breadcrumb[];
  viaShare: boolean;
  realtimeTopic: string;
  publicLink: PublicLink | null;
  shareCount: number;
  /** Comments under a file (0 for a folder). */
  commentCount: number;
}

/** A comment under a file, as the shared comments thread shows it. */
export type FileComment = CommentItem;

/** What a folder upload meets in the target folder (`POST /disk/folders/{id}/upload-check`). */
export interface UploadCheck {
  quotaBytes: number;
  usedBytes: number;
  freeBytes: number;
  maxFiles: number;
  maxFolders: number;
  /** Most levels the uploaded structure may have below the target folder. */
  maxDepth: number;
  conflicts: Conflict[];
}

export type FolderTreeStatus = 'created' | 'merged' | 'renamed' | 'skipped';

export interface FolderTreeEntry {
  path: string;
  /** Null for a skipped folder. */
  folderId: string | null;
  status: FolderTreeStatus;
  name: string;
}

export interface FolderTree {
  folders: FolderTreeEntry[];
}

export interface FolderStats {
  files: number;
  folders: number;
  bytes: number;
}

export interface Version {
  id: string;
  number: number;
  size: number;
  contentType: string;
  createdAt: IsoDateTime;
  createdBy: Person | null;
  isCurrent: boolean;
  url: string | null;
  downloadUrl: string;
}

export interface Share {
  id: string;
  targetType: ShareTargetType;
  targetId: string;
  user: Person | null;
  group: DiskGroup | null;
  role: ShareRole;
  createdAt: IsoDateTime;
  createdBy: Person | null;
  inheritedFrom: Breadcrumb | null;
}

export interface Shares {
  direct: Share[];
  inherited: Share[];
  owner: Person | null;
  group: DiskGroup | null;
  storageKind: StorageKind;
  canManage: boolean;
}

export interface SharedItem {
  item: DiskItem;
  role: ShareRole;
  sharedBy: Person | null;
  sharedAt: IsoDateTime;
  storageKind: StorageKind;
  owner: Person | null;
  group: DiskGroup | null;
}

export interface TrashItem {
  item: DiskItem;
  deletedAt: IsoDateTime;
  deletedBy: Person | null;
  location: Breadcrumb[];
  purgeAt: IsoDateTime;
  canRestore: boolean;
  canPurge: boolean;
}

export interface Conflict {
  name: string;
  existingId: string;
  existingType: ItemType;
  sourceId: string | null;
}

export interface OperationResult {
  items: DiskItem[];
  skipped: string[];
}

export interface DownloadLink {
  url: string;
  fileName: string;
}

export interface PublicItem {
  id: string;
  type: ItemType;
  name: string;
  extension: string | null;
  size: number;
  contentType: string | null;
  updatedAt: IsoDateTime;
  contentUrl: string | null;
  canPreview: boolean;
}

export interface PublicFolder {
  folder: PublicItem;
  path: Breadcrumb[];
  items: PublicItem[];
  zipUrl: string;
}

export interface PublicShare {
  item: PublicItem;
  sharedBy: string | null;
  expiresAt: IsoDateTime | null;
  folder: PublicFolder | null;
  /** Downloads left on a link with a download limit. */
  remainingDownloads?: number | null;
}

export interface ChildrenParams {
  sort?: ItemSort;
  dir?: SortDirection;
  search?: string;
  foldersOnly?: boolean;
}

export interface ShareTarget {
  type: ShareTargetType;
  id: string;
}

// ---- keys -------------------------------------------------------------------------------------------------------

export const diskKeys = {
  all: ['disk'] as const,
  storages: ['disk', 'storages'] as const,
  storage: (id: string) => ['disk', 'storage', id] as const,
  groupStorage: (groupId: string) => ['disk', 'group-storage', groupId] as const,
  usage: (storageId: string) => ['disk', 'usage', storageId] as const,
  folders: ['disk', 'folder'] as const,
  folder: (id: string) => ['disk', 'folder', id] as const,
  childrenAll: ['disk', 'children'] as const,
  children: (id: string, params?: ChildrenParams) =>
    params ? (['disk', 'children', id, params] as const) : (['disk', 'children', id] as const),
  stats: (id: string) => ['disk', 'stats', id] as const,
  items: ['disk', 'item'] as const,
  item: (id: string) => ['disk', 'item', id] as const,
  versions: (id: string) => ['disk', 'versions', id] as const,
  comments: (id: string) => ['disk', 'comments', id] as const,
  shares: (id: string) => ['disk', 'shares', id] as const,
  sharedAll: ['disk', 'shared'] as const,
  shared: (search: string) => ['disk', 'shared', search] as const,
  trashAll: ['disk', 'trash'] as const,
  trash: (storageId: string, search: string) => ['disk', 'trash', storageId, search] as const,
  userFiles: (userId: string) => ['disk', 'user-files', userId] as const,
};

// ---- requests -----------------------------------------------------------------------------------------------------

const enc = encodeURIComponent;
export const CHILDREN_PAGE_SIZE = 100;

export const diskApi = {
  storages: (signal?: AbortSignal) => api.get<Storages>('/disk/storages', { signal }),
  storage: (id: string, signal?: AbortSignal) => api.get<Storage>(`/disk/storages/${enc(id)}`, { signal }),
  groupStorage: (groupId: string, signal?: AbortSignal) =>
    api.get<Storage>(`/disk/storages/groups/${enc(groupId)}`, { signal }),
  usage: (storageId: string, signal?: AbortSignal) =>
    api.get<UsageDetails>(`/disk/storages/${enc(storageId)}/usage`, { signal }),
  folder: (id: string, signal?: AbortSignal) => api.get<Folder>(`/disk/folders/${enc(id)}`, { signal }),
  children: (
    id: string,
    params: ChildrenParams & { page?: number; pageSize?: number },
    signal?: AbortSignal,
  ) =>
    api.get<PagedResult<DiskItem>>(`/disk/folders/${enc(id)}/children`, {
      query: { ...params, foldersOnly: params.foldersOnly || undefined },
      signal,
    }),
  stats: (id: string, signal?: AbortSignal) =>
    api.get<FolderStats>(`/disk/folders/${enc(id)}/stats`, { signal }),
  item: (id: string, signal?: AbortSignal) => api.get<ItemDetails>(`/disk/items/${enc(id)}`, { signal }),
  createFolder: (parentId: string, name: string, conflict?: ConflictMode) =>
    api.post<DiskItem>(`/disk/folders/${enc(parentId)}/folders`, { name, conflict }),
  addFile: (folderId: string, fileId: string, name?: string, conflict?: ConflictMode) =>
    api.post<DiskItem>(`/disk/folders/${enc(folderId)}/files`, { fileId, name, conflict }),
  conflicts: (folderId: string, body: { names?: string[]; itemIds?: string[] }) =>
    api.post<{ conflicts: Conflict[] }>(`/disk/folders/${enc(folderId)}/conflicts`, body),
  uploadCheck: (
    folderId: string,
    body: { fileCount: number; totalBytes: number; folderCount: number; names?: string[] },
  ) => api.post<UploadCheck>(`/disk/folders/${enc(folderId)}/upload-check`, body),
  folderTree: (
    folderId: string,
    body: { paths: string[]; conflict?: ConflictMode; fileCount?: number; totalBytes?: number },
  ) => api.post<FolderTree>(`/disk/folders/${enc(folderId)}/folder-tree`, body),
  comments: (id: string, cursor: string | null, signal?: AbortSignal) =>
    api.get<CursorPage<FileComment>>(`/disk/items/${enc(id)}/comments`, {
      query: { cursor: cursor ?? undefined, limit: 20 },
      signal,
    }),
  createComment: (id: string, bodyHtml: string, attachmentIds: string[]) =>
    api.post<FileComment>(`/disk/items/${enc(id)}/comments`, { bodyHtml, attachmentIds }),
  updateComment: (id: string, commentId: string, bodyHtml: string, attachmentIds: string[]) =>
    api.put<FileComment>(`/disk/items/${enc(id)}/comments/${enc(commentId)}`, { bodyHtml, attachmentIds }),
  deleteComment: (id: string, commentId: string) =>
    api.delete(`/disk/items/${enc(id)}/comments/${enc(commentId)}`),
  rename: (id: string, name: string) => api.patch<DiskItem>(`/disk/items/${enc(id)}`, { name }),
  remove: (itemIds: string[]) => api.post('/disk/items/delete', { itemIds }),
  move: (itemIds: string[], targetFolderId: string, conflict?: ConflictMode) =>
    api.post<OperationResult>('/disk/items/move', { itemIds, targetFolderId, conflict }),
  copy: (itemIds: string[], targetFolderId: string, conflict?: ConflictMode) =>
    api.post<OperationResult>('/disk/items/copy', { itemIds, targetFolderId, conflict }),
  versions: (id: string, signal?: AbortSignal) =>
    api.get<Version[]>(`/disk/items/${enc(id)}/versions`, { signal }),
  addVersion: (id: string, fileId: string) =>
    api.post<DiskItem>(`/disk/items/${enc(id)}/versions`, { fileId }),
  restoreVersion: (id: string, versionId: string) =>
    api.post<DiskItem>(`/disk/items/${enc(id)}/versions/${enc(versionId)}/restore`),
  shares: (id: string, signal?: AbortSignal) => api.get<Shares>(`/disk/items/${enc(id)}/shares`, { signal }),
  addShares: (id: string, targets: ShareTarget[], role: ShareRole) =>
    api.post<Shares>(`/disk/items/${enc(id)}/shares`, { targets, role }),
  updateShare: (id: string, shareId: string, role: ShareRole) =>
    api.put<Shares>(`/disk/items/${enc(id)}/shares/${enc(shareId)}`, { role }),
  removeShare: (id: string, shareId: string) =>
    api.delete<Shares>(`/disk/items/${enc(id)}/shares/${enc(shareId)}`),
  setPublicLink: (id: string, body: SetPublicLinkBody) =>
    api.put<PublicLink>(`/disk/items/${enc(id)}/public-link`, body),
  publicLinkPolicy: (signal?: AbortSignal) => api.get<PublicLinkPolicy>('/disk/settings/public-links', { signal }),
  removePublicLink: (id: string) => api.delete(`/disk/items/${enc(id)}/public-link`),
  shared: (params: { search?: string; page?: number; pageSize?: number }, signal?: AbortSignal) =>
    api.get<PagedResult<SharedItem>>('/disk/shared', { query: { ...params }, signal }),
  userFiles: (userId: string, params: { page?: number; pageSize?: number }, signal?: AbortSignal) =>
    api.get<PagedResult<DiskItem>>(`/disk/users/${enc(userId)}/files`, { query: { ...params }, signal }),
  trash: (
    storageId: string,
    params: { search?: string; page?: number; pageSize?: number },
    signal?: AbortSignal,
  ) =>
    api.get<PagedResult<TrashItem>>(`/disk/storages/${enc(storageId)}/trash`, {
      query: { ...params },
      signal,
    }),
  emptyTrash: (storageId: string) => api.post(`/disk/storages/${enc(storageId)}/trash/empty`),
  restore: (itemIds: string[]) => api.post<{ items: DiskItem[] }>('/disk/trash/restore', { itemIds }),
  purge: (itemIds: string[]) => api.post('/disk/trash/purge', { itemIds }),
  downloadLink: (itemIds: string[]) => api.post<DownloadLink>('/disk/download-link', { itemIds }),
  attach: (itemIds: string[]) => api.post<FileRef[]>('/disk/attachments', { itemIds }),
  publicShare: (token: string, signal?: AbortSignal) =>
    api.get<PublicShare>(`/disk/public/${enc(token)}`, { signal, auth: false }),
  publicFolder: (token: string, folderId: string, signal?: AbortSignal) =>
    api.get<PublicFolder>(`/disk/public/${enc(token)}/folders/${enc(folderId)}`, { signal, auth: false }),
  /** Checks the password of a protected link; the server unlocks this browser for the link (an HttpOnly cookie). */
  unlockPublicLink: (token: string, password: string) =>
    api.post(`/disk/public/${enc(token)}/unlock`, { password }, { auth: false }),
};

// ---- cache helpers ------------------------------------------------------------------------------------------------

/** What a change of items in `folderIds` (and of `itemIds`) makes stale; pure, unit tested. */
export function staleDiskKeys(change: {
  storageId?: string | null;
  folderIds?: readonly string[];
  itemIds?: readonly string[];
  shares?: boolean;
}): (readonly unknown[])[] {
  const keys: (readonly unknown[])[] = [];
  for (const id of change.folderIds ?? []) {
    keys.push(diskKeys.children(id), diskKeys.folder(id), diskKeys.stats(id), diskKeys.item(id));
  }
  for (const id of change.itemIds ?? []) {
    keys.push(
      diskKeys.item(id),
      diskKeys.versions(id),
      diskKeys.shares(id),
      diskKeys.folder(id),
      diskKeys.children(id),
    );
  }
  if (change.storageId) keys.push(diskKeys.usage(change.storageId), [...diskKeys.trashAll, change.storageId]);
  // Search results inside folders, the trash, profile tabs and the storages' usage may show any of these items.
  keys.push(diskKeys.storages, ['disk', 'user-files']);
  if (change.shares) keys.push(diskKeys.sharedAll);
  return keys;
}

export function invalidateDisk(queryClient: QueryClient, change: Parameters<typeof staleDiskKeys>[0]): void {
  for (const queryKey of staleDiskKeys(change)) void queryClient.invalidateQueries({ queryKey });
}

/**
 * Applies `change` to the items of every cached folder listing (`null` drops the item) — optimistic renames and
 * deletions; totals follow the dropped items.
 */
export function patchListedItems(
  queryClient: QueryClient,
  change: (item: DiskItem) => DiskItem | null,
): void {
  queryClient.setQueriesData<InfiniteData<PagedResult<DiskItem>>>(
    { queryKey: diskKeys.childrenAll },
    (data) => {
      if (!data) return data;
      let removed = 0;
      const pages = data.pages.map((page) => {
        const items = page.items.flatMap((item) => {
          const next = change(item);
          if (next === null) removed++;
          return next === null ? [] : [next];
        });
        return { ...page, items };
      });
      return { ...data, pages: pages.map((page) => ({ ...page, total: Math.max(0, page.total - removed) })) };
    },
  );
}

/** After moves and copies across folders/drives every listing may change: refresh the whole module's lists. */
export function invalidateDiskLists(queryClient: QueryClient): void {
  for (const queryKey of [
    diskKeys.childrenAll,
    diskKeys.folders,
    diskKeys.storages,
    diskKeys.trashAll,
    diskKeys.sharedAll,
  ])
    void queryClient.invalidateQueries({ queryKey });
  void queryClient.invalidateQueries({ queryKey: ['disk', 'usage'] });
  void queryClient.invalidateQueries({ queryKey: ['disk', 'stats'] });
  void queryClient.invalidateQueries({ queryKey: ['disk', 'user-files'] });
}

// ---- queries ------------------------------------------------------------------------------------------------------

export function useStorages() {
  return useQuery({
    queryKey: diskKeys.storages,
    queryFn: ({ signal }) => diskApi.storages(signal),
    staleTime: 60_000,
  });
}

/** Who may create or change public links (the administrator's policy) and whether the caller may. */
export function usePublicLinkPolicy() {
  return useQuery({
    queryKey: ['disk', 'public-link-policy'] as const,
    queryFn: ({ signal }) => diskApi.publicLinkPolicy(signal),
    staleTime: 60_000,
  });
}

export function useGroupStorage(groupId: string | undefined) {
  return useQuery({
    queryKey: diskKeys.groupStorage(groupId ?? ''),
    queryFn: ({ signal }) => diskApi.groupStorage(groupId ?? '', signal),
    enabled: !!groupId,
    staleTime: 60_000,
  });
}

export function useUsage(storageId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: diskKeys.usage(storageId ?? ''),
    queryFn: ({ signal }) => diskApi.usage(storageId ?? '', signal),
    enabled: !!storageId && enabled,
  });
}

export function useFolder(id: string | undefined) {
  return useQuery({
    queryKey: diskKeys.folder(id ?? ''),
    queryFn: ({ signal }) => diskApi.folder(id ?? '', signal),
    enabled: !!id,
  });
}

export function useChildren(
  id: string | undefined,
  params: ChildrenParams,
  options: { enabled?: boolean } = {},
) {
  return useInfiniteQuery({
    queryKey: diskKeys.children(id ?? '', params),
    queryFn: ({ signal, pageParam }) =>
      diskApi.children(id ?? '', { ...params, page: pageParam, pageSize: CHILDREN_PAGE_SIZE }, signal),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page * last.pageSize < last.total ? last.page + 1 : undefined),
    enabled: !!id && (options.enabled ?? true),
    placeholderData: keepPreviousData,
  });
}

export function useFolderStats(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: diskKeys.stats(id ?? ''),
    queryFn: ({ signal }) => diskApi.stats(id ?? '', signal),
    enabled: !!id && enabled,
  });
}

export function useItem(id: string | undefined) {
  return useQuery({
    queryKey: diskKeys.item(id ?? ''),
    queryFn: ({ signal }) => diskApi.item(id ?? '', signal),
    enabled: !!id,
  });
}

export function useVersions(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: diskKeys.versions(id ?? ''),
    queryFn: ({ signal }) => diskApi.versions(id ?? '', signal),
    enabled: !!id && enabled,
  });
}

export function useShares(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: diskKeys.shares(id ?? ''),
    queryFn: ({ signal }) => diskApi.shares(id ?? '', signal),
    enabled: !!id && enabled,
  });
}

export function useSharedWithMe(search: string) {
  return useInfiniteQuery({
    queryKey: diskKeys.shared(search),
    queryFn: ({ signal, pageParam }) =>
      diskApi.shared({ search: search || undefined, page: pageParam, pageSize: 50 }, signal),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page * last.pageSize < last.total ? last.page + 1 : undefined),
    placeholderData: keepPreviousData,
  });
}

export function useTrash(storageId: string | undefined, search: string) {
  return useInfiniteQuery({
    queryKey: diskKeys.trash(storageId ?? '', search),
    queryFn: ({ signal, pageParam }) =>
      diskApi.trash(storageId ?? '', { search: search || undefined, page: pageParam, pageSize: 50 }, signal),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page * last.pageSize < last.total ? last.page + 1 : undefined),
    enabled: !!storageId,
    placeholderData: keepPreviousData,
  });
}

export function useUserFiles(userId: string, page: number, pageSize = 20) {
  return useQuery({
    queryKey: [...diskKeys.userFiles(userId), page, pageSize],
    queryFn: ({ signal }) => diskApi.userFiles(userId, { page, pageSize }, signal),
    placeholderData: keepPreviousData,
  });
}

// ---- mutations ----------------------------------------------------------------------------------------------------

/** Mutations with the module's standard cache refresh; errors toast unless the caller says `silent`. */
export function useDiskMutations() {
  const queryClient = useQueryClient();
  const refreshItem = (item: DiskItem, shares = false) =>
    invalidateDisk(queryClient, {
      storageId: item.storageId,
      folderIds: item.parentId ? [item.parentId] : [],
      itemIds: [item.id],
      shares,
    });

  return {
    queryClient,
    refreshItem,
    createFolder: useMutation({
      mutationFn: (v: { parentId: string; name: string; conflict?: ConflictMode }) =>
        diskApi.createFolder(v.parentId, v.name, v.conflict),
      onSuccess: (item) => refreshItem(item),
      meta: { silent: true },
    }),
    rename: useMutation({
      mutationFn: (v: { id: string; name: string }) => diskApi.rename(v.id, v.name),
      // The new name shows at once in every listing; the server's answer (or a refetch on error) settles it.
      onMutate: (v) =>
        patchListedItems(queryClient, (item) => (item.id === v.id ? { ...item, name: v.name } : item)),
      onSuccess: (item) => refreshItem(item, true),
      onError: () => void queryClient.invalidateQueries({ queryKey: diskKeys.childrenAll }),
      meta: { silent: true },
    }),
    remove: useMutation({
      mutationFn: (items: DiskItem[]) => diskApi.remove(items.map((i) => i.id)),
      // Deleted items leave the listings at once (they go to the trash; Undo restores them).
      onMutate: (items) => {
        const ids = new Set(items.map((i) => i.id));
        patchListedItems(queryClient, (item) => (ids.has(item.id) ? null : item));
      },
      onError: () => void queryClient.invalidateQueries({ queryKey: diskKeys.childrenAll }),
      onSuccess: (_data, items) => {
        invalidateDisk(queryClient, {
          storageId: items[0]?.storageId,
          folderIds: [...new Set(items.flatMap((i) => (i.parentId ? [i.parentId] : [])))],
          itemIds: items.map((i) => i.id),
          shares: true,
        });
      },
    }),
    transfer: useMutation({
      mutationFn: (v: {
        mode: 'move' | 'copy';
        itemIds: string[];
        targetFolderId: string;
        conflict?: ConflictMode;
      }) =>
        v.mode === 'move'
          ? diskApi.move(v.itemIds, v.targetFolderId, v.conflict)
          : diskApi.copy(v.itemIds, v.targetFolderId, v.conflict),
      onSuccess: () => invalidateDiskLists(queryClient),
      meta: { silent: true },
    }),
    addVersion: useMutation({
      mutationFn: (v: { id: string; fileId: string }) => diskApi.addVersion(v.id, v.fileId),
      onSuccess: (item) => refreshItem(item),
    }),
    restoreVersion: useMutation({
      mutationFn: (v: { id: string; versionId: string }) => diskApi.restoreVersion(v.id, v.versionId),
      onSuccess: (item) => refreshItem(item),
    }),
    restore: useMutation({
      mutationFn: (itemIds: string[]) => diskApi.restore(itemIds),
      onSuccess: () => invalidateDiskLists(queryClient),
    }),
    purge: useMutation({
      mutationFn: (itemIds: string[]) => diskApi.purge(itemIds),
      onSuccess: () => invalidateDiskLists(queryClient),
    }),
    emptyTrash: useMutation({
      mutationFn: (storageId: string) => diskApi.emptyTrash(storageId),
      onSuccess: () => invalidateDiskLists(queryClient),
    }),
  };
}

export function useShareMutations(item: DiskItem | undefined) {
  const queryClient = useQueryClient();
  const onShares = (shares: Shares) => {
    if (!item) return;
    queryClient.setQueryData(diskKeys.shares(item.id), shares);
    invalidateDisk(queryClient, {
      storageId: item.storageId,
      folderIds: item.parentId ? [item.parentId] : [],
      itemIds: [item.id],
      shares: true,
    });
  };
  const id = item?.id ?? '';
  return {
    add: useMutation({
      mutationFn: (v: { targets: ShareTarget[]; role: ShareRole }) =>
        diskApi.addShares(id, v.targets, v.role),
      onSuccess: onShares,
    }),
    update: useMutation({
      mutationFn: (v: { shareId: string; role: ShareRole }) => diskApi.updateShare(id, v.shareId, v.role),
      onSuccess: onShares,
    }),
    remove: useMutation({
      mutationFn: (shareId: string) => diskApi.removeShare(id, shareId),
      onSuccess: onShares,
    }),
    setLink: useMutation({
      mutationFn: (body: SetPublicLinkBody) => diskApi.setPublicLink(id, body),
      onSuccess: () => {
        if (item)
          invalidateDisk(queryClient, {
            storageId: item.storageId,
            folderIds: item.parentId ? [item.parentId] : [],
            itemIds: [item.id],
          });
      },
    }),
    removeLink: useMutation({
      mutationFn: () => diskApi.removePublicLink(id),
      onSuccess: () => {
        if (item)
          invalidateDisk(queryClient, {
            storageId: item.storageId,
            folderIds: item.parentId ? [item.parentId] : [],
            itemIds: [item.id],
          });
      },
    }),
  };
}
